import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Table,
  Tag,
  notification,
  Popconfirm,
} from "antd";
import { useInviteUserMutation, useInvitations, useRevokeInvitationMutation, useResendInvitationMutation } from "admin/queries";
import { IInvitation } from "admin/services/InvitationService";
import "./InviteUser.scss";

const tenantRoleOptions = [
  { value: "viewer", label: "Viewer" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

const statusColors: Record<string, string> = {
  pending: "blue",
  accepted: "green",
  revoked: "default",
  expired: "orange",
};

export const InviteUser = () => {
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const inviteMutation = useInviteUserMutation();
  const revokeMutation = useRevokeInvitationMutation();
  const resendMutation = useResendInvitationMutation();
  const { data: invitations, isLoading: invitationsLoading } = useInvitations();

  const onFinish = async (values: { email: string; tenantRole: string }) => {
    try {
      await inviteMutation.mutateAsync({
        email: values.email.trim(),
        tenantRole: values.tenantRole,
      });
      api.success({
        duration: 5,
        message: "Invitation sent",
        description: `An invitation has been sent to ${values.email}.`,
      });
      form.resetFields();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ?? error?.message ?? "Failed to send invitation";
      api.error({
        message: "Error",
        description: message,
      });
    }
  };

  const handleResend = async (invitationId: string, email: string) => {
    try {
      await resendMutation.mutateAsync(invitationId);
      api.success({
        duration: 5,
        message: "Invitation resent",
        description: `A new invitation has been sent to ${email}.`,
      });
    } catch (error: any) {
      api.error({
        message: "Error",
        description: error?.response?.data?.message ?? "Failed to resend invitation",
      });
    }
  };

  const handleRevoke = async (invitationId: string) => {
    try {
      await revokeMutation.mutateAsync(invitationId);
      api.success({
        duration: 3,
        message: "Invitation revoked",
        description: "The invitation has been revoked.",
      });
    } catch (error: any) {
      api.error({
        message: "Error",
        description: error?.response?.data?.message ?? "Failed to revoke invitation",
      });
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "tenantRole",
      key: "tenantRole",
      render: (role: string) => role.charAt(0).toUpperCase() + role.slice(1),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={statusColors[status] ?? "default"}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Invited By",
      key: "invitedBy",
      render: (_: any, record: IInvitation) => {
        if (!record.inviter) return "—";
        const name = `${record.inviter.firstName ?? ""} ${record.inviter.lastName ?? ""}`.trim();
        return name || record.inviter.email;
      },
    },
    {
      title: "Sent",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: IInvitation) =>
        record.status === "pending" ? (
          <Space>
            <Popconfirm
              title="Resend this invitation?"
              description="This will send a new invitation email with a fresh link."
              onConfirm={() => handleResend(record.id, record.email)}
              okText="Resend"
              cancelText="Cancel"
            >
              <Button type="link" size="small">
                Resend
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Revoke this invitation?"
              onConfirm={() => handleRevoke(record.id)}
              okText="Revoke"
              cancelText="Cancel"
            >
              <Button type="link" danger size="small">
                Revoke
              </Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <div className="invite-user-container">
      {contextHolder}
      <Typography.Title level={5}>Invite User</Typography.Title>
      <Form
        form={form}
        name="invite-user-form"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        initialValues={{ tenantRole: "viewer" }}
      >
        <Row gutter={{ xs: 4, sm: 8, md: 16, lg: 20 }}>
          <Col span={8}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: "Please enter an email address." },
                { type: "email", message: "Please enter a valid email address." },
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="tenantRole" label="Role">
              <Select options={tenantRoleOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={inviteMutation.isPending}
              >
                Send Invitation
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 16 }}>
        Invitations
      </Typography.Title>
      <Table
        dataSource={invitations ?? []}
        columns={columns}
        rowKey="id"
        loading={invitationsLoading}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        size="small"
      />
    </div>
  );
};
